import {
    Chip,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useClientUsers, useImpersonate } from '../../sic-lib/api/hooks/useSupport';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { requestVerifyAuth } from '../../store/users';
import AxiosErrorAlert from '../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../Shared/ButtonLoading';
import { ButtonLogout } from '../Shared/ButtonLogout';
import DisplayPhoneNumber from '../Shared/DisplayPhoneNumber/DisplayPhoneNumber';

export function SupportDashboard() {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const userFirstName = useAppSelector(state => state.user.userFirstName);
    const userName = useAppSelector(state => state.user.userName);

    const [clientId, setClientId] = useState<string>('');

    const {
        data: clientUsers,
        isFetching: isLoadingClientUsers,
        isSuccess: isClientUsersSuccess,
        isError: isClientUsersError,
        error: clientUsersError,
        refetch: getClientUsers,
    } = useClientUsers(Number(clientId));

    const {
        mutate: impersonate,
        isLoading: isImpersonateLoading,
        isError: isImpersonateError,
        error: impersonateError,
    } = useImpersonate();

    const handleClick = useCallback(
        user => {
            impersonate(
                {
                    clientId: Number(clientId!),
                    userName: user.userName,
                },
                {
                    onSuccess: () => {
                        dispatch(requestVerifyAuth());
                        history.push('/');
                    },
                },
            );
        },
        [clientId, dispatch, impersonate, history],
    );

    return (
        <>
            <Paper sx={{ p: 4 }}>
                <Grid container flexDirection="column" spacing={3} justifyContent="center">
                    <Grid item>
                        <Typography variant="h2">Hello, {userFirstName || userName}</Typography>
                    </Grid>
                    <Grid item display="flex" justifyContent="center">
                        <ButtonLogout />
                    </Grid>
                    <Grid item>
                        <Typography variant="body1">
                            Enter a Client ID to start impersonating
                        </Typography>
                    </Grid>
                    <Grid item container flexDirection="row" spacing={2}>
                        <Grid item flexGrow={1}>
                            <TextField
                                label="Client ID"
                                value={clientId}
                                onChange={e => setClientId(e.target.value)}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item>
                            <ButtonLoading
                                isLoading={isLoadingClientUsers}
                                isSuccess={isClientUsersSuccess}
                                isError={isClientUsersError}
                                disabled={!clientId || isNaN(Number(clientId))}
                                variant="contained"
                                color="primary"
                                successLabel="Search"
                                onClick={() => getClientUsers()}
                            >
                                Search
                            </ButtonLoading>
                        </Grid>
                    </Grid>
                    {isClientUsersError && (
                        <Grid item>
                            <AxiosErrorAlert error={clientUsersError} />
                        </Grid>
                    )}
                    {!isLoadingClientUsers && !!clientUsers?.length && (
                        <Grid item>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                            }}
                                        >
                                            <TableCell>UserName</TableCell>
                                            <TableCell>FirstName</TableCell>
                                            <TableCell>LastName</TableCell>
                                            <TableCell>Virtual Number</TableCell>
                                            <TableCell>UserEmail</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clientUsers.map(user => (
                                            <TableRow
                                                key={user.userName}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                }}
                                            >
                                                <TableCell>{user.userName}</TableCell>
                                                <TableCell>{user.firstName}</TableCell>
                                                <TableCell>{user.lastName}</TableCell>
                                                <TableCell>
                                                    <Stack>
                                                        {user.hasDialer && (
                                                            <>
                                                                <DisplayPhoneNumber
                                                                    phoneNumber={
                                                                        user.twilioPhoneNumber
                                                                    }
                                                                />
                                                                {user.twilioPhoneNumber &&
                                                                    user.dialerIsSic && (
                                                                        <Chip
                                                                            label="SIC"
                                                                            color="primary"
                                                                            variant="outlined"
                                                                            size="small"
                                                                        />
                                                                    )}
                                                                {user.twilioPhoneNumber &&
                                                                    !user.dialerIsSic && (
                                                                        <Chip
                                                                            label="Engage"
                                                                            color="secondary"
                                                                            variant="outlined"
                                                                            size="small"
                                                                        />
                                                                    )}
                                                            </>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography>{user.userEmailId}</Typography>
                                                    {user.userEmailBroken && (
                                                        <>
                                                            <Chip
                                                                label="Broken"
                                                                color="error"
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <ButtonLoading
                                                        isLoading={isImpersonateLoading}
                                                        isError={isImpersonateError}
                                                        variant="outlined"
                                                        color="secondary"
                                                        onClick={() => handleClick(user)}
                                                        size="small"
                                                    >
                                                        Impersonate
                                                    </ButtonLoading>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    )}
                    {isImpersonateLoading && (
                        <Grid item>
                            <AxiosErrorAlert error={impersonateError} />
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </>
    );
}
